/**
 * The voice agent's binding rules (realtime voice calls, D3).
 *
 * A `pilot_voice` agent is the voice layer of a live call: the provider's model listens and
 * speaks, and its instructions tell it to delegate answers to the conversation's pilot. It runs on a `Realtime`
 * connection and nothing else, and a `Realtime` connection is never an LLM connection, so no
 * other role may use one. Both halves are one rule, stated here once for every surface that
 * judges or offers a binding: the HubDO agent and connection writers (the chokepoint every
 * writer reaches), CI push's pre-write check, and the agent editor's connection picker.
 *
 * RN-safe and pure: it reads the connector TYPE the caller resolved from the catalog, never a
 * connection row.
 */

import type { AgentRole } from '../constants';
import type { ConnectorType } from './types';

/** The one role that binds a `Realtime` connection. */
export const VOICE_AGENT_ROLE = 'pilot_voice' satisfies AgentRole;

/** The one connector type a voice agent binds. */
export const VOICE_AGENT_CONNECTOR_TYPE = 'Realtime' satisfies ConnectorType;

/** The voice agent's `language` setting that speaks each caller's own profile language. */
export const CALLER_LANGUAGE = 'caller';

export function isVoiceAgentRole(role: unknown): boolean {
  return role === VOICE_AGENT_ROLE;
}

export function isRealtimeConnectorType(connectorType: unknown): boolean {
  return connectorType === VOICE_AGENT_CONNECTOR_TYPE;
}

/**
 * The connector type an agent of `role` binds: `Realtime` for a `pilot_voice` agent, `Agent`
 * (an LLM connection) for every other role — the list an editor or API offers it.
 */
export function bindableConnectorType(role: unknown): ConnectorType {
  return isVoiceAgentRole(role) ? VOICE_AGENT_CONNECTOR_TYPE : 'Agent';
}

/**
 * Whether an agent write is one the voice-calls rollout gate judges: it brings a `pilot_voice`
 * agent into being (a create, or a role change to it) or turns one on. `stored` is null on a
 * create; `enabled` on each side is already read the way its writer stores it.
 *
 * Everything else the gate lets through while it is closed — the harness gate's carve-outs:
 * turning a voice agent off, editing one without turning it on, and (never reaching here)
 * deleting one.
 */
export function voiceAgentWriteNeedsGate(
  stored: { agent_role: unknown; enabled: boolean } | null,
  effective: { agent_role: unknown; enabled: boolean },
): boolean {
  if (!isVoiceAgentRole(effective.agent_role)) return false;
  if (!stored || !isVoiceAgentRole(stored.agent_role)) return true;
  return effective.enabled && !stored.enabled;
}

/**
 * Refusal: a text eval naming a `pilot_voice` agent as its responder. The voice layer answers
 * live calls only, and no text turn ever runs it.
 */
export const VOICE_AGENT_TEXT_EVAL_REFUSAL =
  'A pilot_voice agent answers live calls only, so it cannot be a text eval\'s responder';

/** Refusal: a `pilot_voice` agent naming a connection that is not a `Realtime` one. */
export const VOICE_AGENT_NEEDS_REALTIME_CONNECTION =
  'A pilot_voice agent can use only a Realtime connection';

/** Refusal: a `Realtime` connection used by an agent that is not a `pilot_voice` one. */
export const REALTIME_CONNECTION_NEEDS_VOICE_AGENT =
  'A Realtime connection can be used only by a pilot_voice agent';

/**
 * What the binding rules refuse an agent's (role, connection) pair with, or null.
 *
 * - `connection_id` absent, null or empty: no binding, nothing to judge. An agent may be
 *   saved without a connection and bound later, whatever its role.
 * - `connector_type`: the catalog type of that connection's connector, as the caller
 *   resolved it. `null` means the caller could not resolve one (the hub holds no such
 *   connection, or the catalog does not know its connector): that is not a `Realtime`
 *   connection, so a voice agent naming it is refused — it fails closed — and any other
 *   role is left as it was, which is how an agent naming an unknown connection has always
 *   been treated.
 */
export function voiceAgentBindingRefusal(binding: {
  agent_role: unknown;
  connection_id: unknown;
  connector_type: string | null | undefined;
}): string | null {
  if (typeof binding.connection_id !== 'string' || binding.connection_id.length === 0) return null;
  const voice = isVoiceAgentRole(binding.agent_role);
  const realtime = isRealtimeConnectorType(binding.connector_type);
  if (voice && !realtime) return VOICE_AGENT_NEEDS_REALTIME_CONNECTION;
  if (!voice && realtime) return REALTIME_CONNECTION_NEEDS_VOICE_AGENT;
  return null;
}
